import React, { useEffect, useRef, useState } from "react";
import { LINEAGE } from "./data";
import { useRevealGroup } from "../../hooks/useReveal";

/**
 * Section ⑤ — Who We Are / Our Legacy intro + animated lineage tree.
 *
 * Light warm-linen canvas, near-black headlines, grey body, deep-green
 * eyebrow labels. Lineage tree nodes animate in sequentially (300ms
 * stagger) once the tree scrolls into view; the vertical gold line
 * draws downwards over the same total span.
 */
const STAGGER_MS = 300;
const LINE_DURATION_MS = STAGGER_MS * LINEAGE.length; // 8 nodes × 300ms

const About = () => {
  const textRef = useRevealGroup(".reveal");
  const treeRef = useRef(null);
  const [treeVisible, setTreeVisible] = useState(false);

  useEffect(() => {
    if (!treeRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setTreeVisible(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    io.observe(treeRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={textRef}
      className="py-24 lg:py-36 overflow-hidden"
      style={{ backgroundColor: "#FAF6EF" }}
      data-testid="about-section"
    >
      <div className="max-w-[1340px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 relative" style={{ zIndex: 1 }}>
        {/* Text column */}
        <div className="lg:col-span-7">
          <div className="reveal flex items-center gap-4 mb-8">
            <span className="block w-10 h-px" style={{ backgroundColor: "#0A3728" }} />
            <span
              className="font-ui uppercase track-eyebrow"
              style={{ fontSize: 9, color: "#0A3728", letterSpacing: "0.48em" }}
            >
              Our Legacy
            </span>
          </div>
          <h2
            className="reveal delay-1 font-display font-medium leading-[1.05]"
            style={{ fontSize: "clamp(38px, 5vw, 60px)", color: "#0A0A0A" }}
          >
            Built on Land.<br />
            Carried by{" "}
            <span
              className="font-italic italic font-normal"
              style={{ color: "#0A3728" }}
            >
              Generations.
            </span>
          </h2>

          <div
            className="reveal delay-2 mt-10 max-w-[640px] space-y-6 font-body text-[15px] leading-[1.9]"
            style={{ color: "#3A3A3A" }}
          >
            <p>
              House of Numbardar is built on a legacy of land, trust, and custodianship that traces back to the late 18th century. The title of <em>Numbardar</em> was not merely a designation — it was a responsibility. A Numbardar was entrusted with the stewardship of land, the integrity of records, and the confidence of the people.
            </p>
            <p>
              Founded in spirit by{" "}
              <strong className="font-display font-semibold" style={{ color: "#0A0A0A" }}>Dharam Singh</strong>{" "}
              (born 1795), the First Numbardar of the lineage, this custodianship has carried forward through eight generations. Today, the current generation reimagines this legacy for modern India — bringing structure, insight, and strategic thinking to real estate and wealth creation.
            </p>
            <p
              className="font-italic italic pl-5 border-l-2"
              style={{ color: "#0A0A0A", fontSize: 16, borderColor: "#0A3728" }}
            >
              We do not merely deal in property. We uphold a legacy of land, trust, and generational wealth.
            </p>
          </div>
        </div>

        {/* Lineage tree column */}
        <div className="lg:col-span-5 relative">
          <div className="reveal mb-10">
            <div
              className="font-ui uppercase track-eyebrow mb-4"
              style={{ fontSize: 9, color: "#0A3728", letterSpacing: "0.48em" }}
            >
              The Family Lineage
            </div>
            <div
              className="font-display"
              style={{ color: "#0A0A0A", fontSize: 22, fontWeight: 500 }}
            >
              Eight Generations of Custodianship
            </div>
          </div>

          <div
            ref={treeRef}
            className="relative pl-10"
            data-testid="lineage-tree"
          >
            {/* Vertical gold line — draws down over LINE_DURATION_MS */}
            <span
              aria-hidden="true"
              className="absolute left-3 top-2 w-px"
              style={{
                bottom: 8,
                background: "#C8A95A",
                transformOrigin: "top",
                transform: treeVisible ? "scaleY(1)" : "scaleY(0)",
                transition: `transform ${LINE_DURATION_MS}ms cubic-bezier(0.22,1,0.36,1)`,
              }}
            />

            {LINEAGE.map((node, idx) => (
              <div
                key={idx}
                className="relative pb-9 last:pb-0"
                style={{
                  opacity: treeVisible ? 1 : 0,
                  transform: treeVisible ? "translateX(0)" : "translateX(-16px)",
                  transition: "opacity 500ms ease, transform 500ms ease",
                  transitionDelay: `${idx * STAGGER_MS}ms`,
                }}
                data-testid={`lineage-node-${idx}`}
              >
                {/* Node marker */}
                <span
                  className="absolute -left-[28px] top-2 w-2.5 h-2.5 rotate-45"
                  style={
                    node.branch
                      ? { border: "1px solid #C8A95A", backgroundColor: "transparent" }
                      : { backgroundColor: "#C8A95A" }
                  }
                />
                {/* Horizontal connector */}
                <span
                  className="absolute -left-[18px] top-[14px] w-4 h-px"
                  style={{ backgroundColor: "rgba(200,169,90,0.55)" }}
                />

                <div className="flex items-center gap-3">
                  <span
                    className="font-ui w-8"
                    style={{ fontSize: 10, letterSpacing: "0.32em", color: "#0A3728" }}
                  >
                    {node.gen}
                  </span>
                  <span
                    className="font-display font-medium"
                    style={{ fontSize: 14, color: "#0A0A0A" }}
                  >
                    {node.name}
                  </span>
                  {node.sibling && (
                    <>
                      <span
                        style={{
                          color: "#C8A95A",
                          fontSize: 12,
                          margin: "0 6px",
                          lineHeight: 1,
                        }}
                      >
                        ✦
                      </span>
                      <span
                        className="font-display font-medium"
                        style={{ fontSize: 14, color: "#0A0A0A" }}
                      >
                        {node.sibling.name}
                      </span>
                    </>
                  )}
                </div>
                <div
                  className="font-ui uppercase mt-1.5 ml-11"
                  style={{ fontSize: 9, letterSpacing: "0.3em", color: "#8C7E6A" }}
                >
                  {node.sibling ? (
                    <>
                      <span style={{ whiteSpace: "nowrap" }}>
                        <span
                          className="num-editorial align-baseline"
                          style={{ fontSize: 16, marginRight: 8, letterSpacing: "-0.02em", color: "#0A0A0A", display: "inline" }}
                        >
                          {node.year.replace(/[^0-9]/g, "")}
                        </span>
                        <span style={{ marginRight: 24 }}>·</span>
                        <span
                          className="num-editorial align-baseline"
                          style={{ fontSize: 16, marginRight: 8, letterSpacing: "-0.02em", color: "#0A0A0A", display: "inline" }}
                        >
                          {node.sibling.year}
                        </span>
                      </span>
                      <br />
                      {node.era}
                    </>
                  ) : (
                    <>
                      <span
                        className="num-editorial align-baseline"
                        style={{ fontSize: 16, marginRight: 8, letterSpacing: "-0.02em", color: "#0A0A0A", display: "inline" }}
                      >
                        {node.year.replace(/[^0-9]/g, "")}
                      </span>
                      · {node.era}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
